import { useQuery } from "@apollo/client";
import { Controller, useFormContext, useFormState } from "react-hook-form";

import Select from "@/components/Select";
import SubSectionHeading from "@/components/SubSectionHeading";
import {
  nullableBooleanToYesNoNullOption,
  yesNoNullOptionToNullableBoolean
} from "@/features/facility/components/ContactDetails/forms/validation";

import { FACILITY_ENUM_OPTIONS_QUERY } from "../../queries";
import { isCourseTypeRequired, type CreateFacilityFormValues } from "../../_utils/createFacilityForm";
import { EnumSelectField, FormTextField, optionsWithEmpty } from "./shared";

export default function CourseDetailsStep() {
  const { data: optionsData } = useQuery(FACILITY_ENUM_OPTIONS_QUERY);
  const { control, watch } = useFormContext<CreateFacilityFormValues>();
  const { isSubmitting } = useFormState({ control });
  const courseTypeRequired = isCourseTypeRequired(watch("facilityType"));

  return (
    <div className="flex flex-col">
      <p className="text-title-large mb-6">Add optional course and membership details.</p>

      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-4">
          <SubSectionHeading>Course Details</SubSectionHeading>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <EnumSelectField
              name="courseType"
              label="Course Type"
              options={optionsWithEmpty(optionsData?.courseTypeOptions.enumValues)}
              required={courseTypeRequired}
            />
            <EnumSelectField
              name="operationType"
              label="Operation Type"
              options={optionsWithEmpty(optionsData?.operationTypeOptions.enumValues)}
              required
            />
            <FormTextField
              className="w-full"
              name="lengthOfSeasonInMonths"
              label="Length of Season (Months)"
              type="number"
            />
            <FormTextField className="w-full" name="holesInRegulation" label="Total Regulation Holes" type="number" />
            <Controller
              name="practiceRangeAvailable"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => (
                <Select
                  label="Practice Range Available"
                  value={nullableBooleanToYesNoNullOption(value as boolean | null)}
                  onSelect={(value) => onChange(yesNoNullOptionToNullableBoolean(value))}
                  options={[{ value: "", label: "--" }, { value: "Yes" }, { value: "No" }]}
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  errorText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="simulatorAvailable"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => (
                <Select
                  label="Simulator Available"
                  value={nullableBooleanToYesNoNullOption(value as boolean | null)}
                  onSelect={(value) => onChange(yesNoNullOptionToNullableBoolean(value))}
                  options={[{ value: "", label: "--" }, { value: "Yes" }, { value: "No" }]}
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  errorText={fieldState.error?.message}
                />
              )}
            />
            <FormTextField className="w-full" name="golfCartBrand" label="Golf Cart Brand" />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <SubSectionHeading>Membership Details</SubSectionHeading>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormTextField className="w-full" name="membershipTotal" type="number" label="Total Membership" />
            <FormTextField className="w-full" name="membershipMen" type="number" label="Membership - Men" />
            <FormTextField className="w-full" name="membershipWomen" type="number" label="Membership - Women" />
            <FormTextField className="w-full" name="membershipJunior" type="number" label="Membership - Juniors" />
            <FormTextField className="w-full" name="membershipSenior" type="number" label="Membership - Seniors" />
          </div>
        </div>
      </div>
    </div>
  );
}
